import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Button, Table, Tag } from "antd";
import type { ColumnsType } from "antd/lib/table";
import PageTitle from "@component/PageTitle";
import timeUtils from "@utils/timeUtils";
import { useAppDispatch } from "src/store";
import { fetchContractList } from "@redux/SCMSlice";
import { SCMContract } from "@api/scmAPI";
import { useSelector } from "react-redux";
import { RootState } from "@redux/rootReducer";
import fontStyle from "src/styles/fontStyle";
import SCMContractViewModal from "./SCMContractViewModal";
import { CONTRACT_MODE_TEXT, CONTRACT_STATUS, CONTRACT_STATUS_TEXT, getStatusColor } from "./utils";

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;
const VendorTitle = styled.h3`
  padding: 24px;
  ${fontStyle("24px", "24px", "bold")};
`;
const ContentWrapper = styled.div`
  background: #ffffff;
  border-radius: 2px;
  padding: 24px;
`;

const ContentTitle = styled.h3`
  margin-bottom: 16px;
`;

export default function SCMContractDetail() {
  const { ubn } = useParams();
  const dispatch = useAppDispatch();
  const { isFetching } = useSelector((state: RootState) => state.SCMSlice);
  const [contracts, setContracts] = useState<SCMContract[]>([]);
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const openView = useCallback((id: number) => {
    setSelectedId(id);
    setViewVisible(true);
  }, []);

  const closeView = () => {
    setViewVisible(false);
    setSelectedId(null);
  };

  const columns: ColumnsType<SCMContract> = [
    {
      title: "合約ID",
      dataIndex: "id",
      key: "id",
      width: 90,
      render: (id: number) => (
        <Button type="link" size="small" onClick={() => openView(id)}>
          {`#${id}`}
        </Button>
      ),
    },
    {
      title: "合作類型",
      dataIndex: "contractMode",
      key: "contractMode",
      align: "center",
      width: 160,
      render: (mode: number | null) => (mode ? CONTRACT_MODE_TEXT[mode] : "-"),
    },
    {
      title: "生效日",
      key: "startDate",
      dataIndex: "startDate",
      align: "center",
    },
    {
      title: "結束日",
      key: "endDate",
      dataIndex: "endDate",
      align: "center",
    },
    {
      title: "狀態",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 100,
      render: (status: number) => <Tag color={getStatusColor(status)}>{CONTRACT_STATUS_TEXT[status] || "-"}</Tag>,
    },

    {
      title: "最後更新者",
      dataIndex: "lastUpdatedBy",
      key: "lastUpdatedBy",
      width: 160,
      align: "center",
      render: (text: string | null) => text || "-",
    },
    {
      title: "建立時間",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => (text ? timeUtils.formatToTimestamp(text, "YYYY/MM/DD HH:mm") : "-"),
    },
  ];
  const sortedContractList = useMemo(() => {
    const activeContracts: SCMContract[] = [];
    const inactiveContracts: SCMContract[] = [];
    const historyContracts: SCMContract[] = [];
    contracts.forEach((contract) => {
      switch (contract.status) {
        case CONTRACT_STATUS.ACTIVE:
          activeContracts.push(contract);
          break;
        case CONTRACT_STATUS.INACTIVE:
          inactiveContracts.push(contract);
          break;
        case CONTRACT_STATUS.ENDED:
        case CONTRACT_STATUS.DISABLED:
        default:
          historyContracts.push(contract);
          break;
      }
    });
    return { activeContracts, inactiveContracts, historyContracts };
  }, [contracts]);

  useEffect(() => {
    const load = async () => {
      if (!ubn) return;
      const res = await dispatch(fetchContractList({ limit: 200, offset: 0, ubn })).unwrap();
      setContracts(res.results);
    };
    load();
  }, [dispatch, ubn]);

  return (
    <Wrapper>
      <PageTitle title={`合約總覽 ${contracts[0]?.name}`} />
      <VendorTitle>{`合約總覽 - ${contracts[0]?.name || ""}`}</VendorTitle>
      <ContentWrapper>
        <ContentTitle>目前合約</ContentTitle>
        <Table
          loading={isFetching}
          columns={columns}
          dataSource={sortedContractList.activeContracts}
          rowKey="id"
          pagination={false}
        />
      </ContentWrapper>
      <ContentWrapper>
        <ContentTitle>已排定合約</ContentTitle>
        <Table
          loading={isFetching}
          columns={columns}
          dataSource={sortedContractList.inactiveContracts}
          rowKey="id"
          pagination={false}
        />
      </ContentWrapper>
      <ContentWrapper>
        <ContentTitle>歷史合約</ContentTitle>
        <Table
          loading={isFetching}
          columns={columns}
          dataSource={sortedContractList.historyContracts}
          rowKey="id"
          pagination={false}
        />
      </ContentWrapper>
      {selectedId && <SCMContractViewModal visible={viewVisible} contractId={selectedId} onClose={closeView} />}
    </Wrapper>
  );
}
